import { getBootstrapTooltip } from './bootstrap-runtime';

type AdminTheme = 'light' | 'dark';

const STORAGE_KEY = 'nais-admin-theme';

export function initAdminTheme(): void {
    const body = document.body;
    if (!body.classList.contains('nais-admin')) {
        return;
    }

    const storedTheme = readStoredTheme();
    const initialTheme = storedTheme ?? readDocumentTheme();
    applyTheme(initialTheme);

    const toggle = document.querySelector<HTMLButtonElement>('[data-nais-theme-toggle]');
    if (!toggle) {
        return;
    }

    updateToggle(toggle, initialTheme);
    toggle.addEventListener('click', () => {
        const nextTheme: AdminTheme = readDocumentTheme() === 'dark' ? 'light' : 'dark';
        applyTheme(nextTheme);
        writeStoredTheme(nextTheme);
        updateToggle(toggle, nextTheme);
    });
}

function applyTheme(theme: AdminTheme): void {
    const root = document.documentElement;
    const body = document.body;

    root.dataset.naisTheme = theme;
    root.dataset.bsTheme = theme;
    body.classList.toggle('nais-theme-light', theme === 'light');
    body.classList.toggle('nais-theme-dark', theme === 'dark');
    document.dispatchEvent(new CustomEvent('nais:themechange', { detail: { theme } }));
}

function updateToggle(toggle: HTMLButtonElement, theme: AdminTheme): void {
    const label = theme === 'dark'
        ? (toggle.dataset.labelLight ?? '')
        : (toggle.dataset.labelDark ?? '');

    toggle.setAttribute('aria-pressed', theme === 'dark' ? 'true' : 'false');
    toggle.setAttribute('aria-label', label);
    toggle.setAttribute('data-bs-title', label);

    const tooltip = getBootstrapTooltip().getInstance(toggle);
    if (!tooltip) {
        toggle.setAttribute('title', label);
        return;
    }

    toggle.removeAttribute('title');
    toggle.setAttribute('data-bs-original-title', label);
    tooltip.setContent({ '.tooltip-inner': label });
}

function readDocumentTheme(): AdminTheme {
    return document.documentElement.dataset.naisTheme === 'dark' ? 'dark' : 'light';
}

function readStoredTheme(): AdminTheme | null {
    try {
        const value = window.localStorage.getItem(STORAGE_KEY);
        return value === 'dark' || value === 'light' ? value : null;
    } catch {
        return null;
    }
}

function writeStoredTheme(theme: AdminTheme): void {
    try {
        window.localStorage.setItem(STORAGE_KEY, theme);
    } catch {
        // Theme switching still works for this page when storage is unavailable.
    }
}
